import _ from "lodash";
import {DateTime} from "luxon";
import {usePage} from "@inertiajs/vue3";
import {computed} from "vue";
import { trans } from 'laravel-vue-i18n';

export function getPrettyHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.querySelectorAll('span.mention[data-denotation-char="%"]').forEach(mention => {
        const dataId = mention.getAttribute('data-id');
        const replacement = `%${dataId}%`;
        mention.replaceWith(replacement);
    });
    return doc.body.innerHTML;
}

export function brightnessByColor(color) {
    const hasFullSpec = color.length === 7;
    const m = color.substr(1).match(hasFullSpec ? /(\S{2})/g : /(\S{1})/g);
    if (m) {
        let r = parseInt(m[0] + (hasFullSpec ? '' : m[0]), 16);
        let g = parseInt(m[1] + (hasFullSpec ? '' : m[1]), 16);
        let b = parseInt(m[2] + (hasFullSpec ? '' : m[2]), 16);
        return ((r * 299) + (g * 587) + (b * 114)) / 1000;
    }
    if (typeof r != "undefined") return ((r * 299) + (g * 587) + (b * 114)) / 1000;
}

export function getServiceIcon(location) {
    if (location?.type === 'in_person') {
        return 'pi pi-map-marker';
    }

    switch (location?.service) {
        case 'ms_teams':
            return 'bi bi-microsoft-teams';
        case 'google_meet':
            return 'pi pi-google';
        case 'whatsapp':
            return 'bi bi-whatsapp';
        case 'phone_call':
            return 'pi pi-phone';
        default:
            return 'pi pi-map-marker';
    }
}

export function getFormType(formType) {
    switch (formType) {
        case 'service':
            return { icon: 'pi pi-briefcase', label: trans('forms.type_service') }
        case 'rental':
            return { icon: 'pi pi-box', label: trans('forms.type_rental') }
        case 'accommodation':
            return { icon: 'pi pi-home', label: trans('forms.type_accommodation') }
        default:
        case 'personal':
            return { icon: 'pi pi-user', label: trans('forms.type_personal') }
    }
}

export function getServiceName(location) {
    if (location?.type === 'in_person') {
        const a = JSON.parse(location?.address)?.address;

        let address = [];
        if (a?.road)
            address.push(a?.road + (a?.house_number ? ' ' + a?.house_number : ''))

        if (a?.town)
            address.push(a?.town)

        if (a?.country)
            address.push(a?.country)
        return address.join(', ');
    }
    switch (location?.service) {
        case 'ms_teams':
            return 'Microsoft Teams';
        case 'phone_call':
            return 'Telefonní hovor';
        case 'google_meet':
            return 'Google Meet';
        case 'whatsapp':
            return 'WhatsApp';
        default:
            const a = JSON.parse(location?.address)?.address;

            let address = [];
            if (a?.road)
                address.push(a?.road + (a?.house_number ? ' ' + a?.house_number : ''))

            if (a?.town)
                address.push(a?.town)

            if (a?.country)
                address.push(a?.country)
            return address.join(', ');
    }
}

export function getInitials(name) {
    const parts = name?.split(' ');
    if (!parts)
    {
        return '';
    }
    return _.upperCase(parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : ''));
}

export function percentToHex(percent) {
    const decimal = Math.round((percent / 100) * 255);
    const hex = decimal.toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
}

export function groupSlotsByTime(slots) {
    const groupedSlots = {};
    slots.forEach(slot => {
        const key = `${slot.valid_from}/${slot.valid_to}`;
        if (!groupedSlots[key]) {
            groupedSlots[key] = [];
        }
        groupedSlots[key].push(slot.name);
    });
    return groupedSlots;
}

const AVATAR_PALETTE = [
    '#2A6FDB', '#8E5BC9', '#1F8A5B', '#C73E8C',
    '#D97757', '#0D9488', '#5B5BD6', '#D9544A',
]

export function getAvatarColor(name) {
    if (!name) { return AVATAR_PALETTE[0] }
    let hash = 0
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length]
}

export function timeAgo(dateString) {
    if (!dateString) { return null }
    const dt = DateTime.fromISO(dateString)
    const now = DateTime.now()
    const diff = now.diff(dt, ['years', 'months', 'days', 'hours']).toObject()

    const years = Math.floor(diff.years)
    if (years >= 1) { return trans(years === 1 ? 'common.time_ago_year' : 'common.time_ago_years', { count: years }) }
    const months = Math.floor(diff.months)
    if (months >= 1) { return trans(months === 1 ? 'common.time_ago_month' : 'common.time_ago_months', { count: months }) }
    const weeks = Math.floor(diff.days / 7)
    if (diff.days >= 7) { return trans(weeks === 1 ? 'common.time_ago_week' : 'common.time_ago_weeks', { count: weeks }) }
    if (diff.days >= 2) { return trans('common.time_ago_days', { count: Math.floor(diff.days) }) }
    if (diff.days >= 1) { return trans('common.yesterday') }
    return trans('common.today')
}

export function formatCzk(amount) {
    if (!amount && amount !== 0) { return null }
    const num = Math.round(Number(amount))
    return num.toLocaleString('cs-CZ') + ' Kč'
}

export function useFormatDate() {
    const { locale = 'cs-CZ', timezone = 'Europe/Prague' } = usePage().props
    const shortLocale = locale.split('-')[0] // cs-CZ → cs

    const formatDate = (dateString, format = 'd. LLL HH:mm') => {
        return DateTime
            .fromISO(dateString, { zone: timezone })
            .setLocale(shortLocale)
            .toFormat(format)
    }

    const formatFullDate = (dateString) => {
        return formatDate(dateString, "d. LLLL yyyy 'v' HH:mm")
    }

    const formatTimeOnly = (dateString) => {
        return formatDate(dateString, 'HH:mm')
    }

    return {
        formatDate,
        formatFullDate,
        formatTimeOnly,
    }
}
